import React, { useState, useEffect } from "react";
import TextBox2 from "../TextBox2";
import edit from "../../icons/Edit.svg";
import back from "../../icons/left.png";
import { useDispatch, useSelector } from "react-redux";
import { updateHostProfile, updateEntryInstructionEmailPrefs } from "../../store/thunks/hostActions";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";

function ProfileComponent({ Info }) {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const { hid } = params;

  const [editMode, setEditMode] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);

  const {
    loading: loadingUpdate,
    success: successUpdate,
    error: errorUpdate,
  } = useSelector((state) => state.hostUpdate);

  const {
    receiveEntryInstructionsEmails,
    entryInstructionsAdditionalEmails = [],
    loading: loadingFlags,
  } = useSelector((state) => state.hostFlags);

  const [newNotificationEmail, setNewNotificationEmail] = useState("");
  const [entryEmailError, setEntryEmailError] = useState("");
  const [entryEmailSuccess, setEntryEmailSuccess] = useState("");

  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [credentials, setCredentials] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
  });

  const handleUpdate = () => {
    const profileCredentials = { address, ...credentials };
    dispatch(updateHostProfile({ profileCredentials, hid }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

  const handleToggleEntryInstructions = () => {
    setEntryEmailError("");
    setEntryEmailSuccess("");
    dispatch(updateEntryInstructionEmailPrefs({
      hid,
      receiveEntryInstructionsEmails: !receiveEntryInstructionsEmails,
      entryInstructionsAdditionalEmails,
    })).then((result) => {
      if (result.meta.requestStatus === "fulfilled" && result.payload?.success) {
        setEntryEmailSuccess(result.payload.msg || "Saved.");
      } else if (result.meta.requestStatus === "rejected" && result.payload?.error) {
        setEntryEmailError(result.payload.error);
      }
    });
  };

  const handleAddNotificationEmail = () => {
    const email = newNotificationEmail.trim();
    setEntryEmailError("");
    setEntryEmailSuccess("");
    if (!email) {
      setEntryEmailError("Enter an email address.");
      return;
    }
    if (!isValidEmail(email)) {
      setEntryEmailError("Enter a valid email address.");
      return;
    }
    if (entryInstructionsAdditionalEmails.includes(email)) {
      setEntryEmailError("This email is already in the list.");
      return;
    }
    const updated = [...entryInstructionsAdditionalEmails, email];
    dispatch(updateEntryInstructionEmailPrefs({
      hid,
      receiveEntryInstructionsEmails,
      entryInstructionsAdditionalEmails: updated,
    })).then((result) => {
      if (result.meta.requestStatus === "fulfilled" && result.payload?.success) {
        setNewNotificationEmail("");
        setEntryEmailSuccess(result.payload.msg || "Email added.");
      } else if (result.meta.requestStatus === "rejected" && result.payload?.error) {
        setEntryEmailError(result.payload.error);
      }
    });
  };

  const handleRemoveNotificationEmail = (emailToRemove) => {
    setEntryEmailError("");
    setEntryEmailSuccess("");
    const updated = entryInstructionsAdditionalEmails.filter((e) => e !== emailToRemove);
    dispatch(updateEntryInstructionEmailPrefs({
      hid,
      receiveEntryInstructionsEmails,
      entryInstructionsAdditionalEmails: updated,
    })).then((result) => {
      if (result.meta.requestStatus === "fulfilled" && result.payload?.success) {
        setEntryEmailSuccess(result.payload.msg || "Email removed.");
      } else if (result.meta.requestStatus === "rejected" && result.payload?.error) {
        setEntryEmailError(result.payload.error);
      }
    });
  };

  useEffect(() => {
    if (errorUpdate) {
      setEditMode(true);
      setIsReadOnly(false);
    } else if (successUpdate) {
      setEditMode(false);
      setIsReadOnly(true);
    }
    if (Info) {
      setAddress({ ...Info.address });
      setCredentials({
        firstName: Info.firstName,
        lastName: Info.lastName,
        email: Info.email,
        phoneNumber: Info.phoneNumber,
        password: "",
      });
    }
  }, [Info, dispatch, errorUpdate, successUpdate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-xl text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
            aria-label="Go back"
          >
            <img src={back} alt="" className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Profile Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          {loadingUpdate && (
            <CircularProgress color="inherit" className="mr-2" size={20} />
          )}
          {!editMode ? (
            <button
              type="button"
              onClick={() => {
                setEditMode(true);
                setIsReadOnly(false);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-green-700 rounded-xl border border-green-200/60 hover:border-green-400 hover:bg-green-50/50 transition-all duration-300 shadow-sm hover:shadow-md font-medium text-sm"
            >
              <img src={edit} alt="" className="w-4 h-4 opacity-90 [filter:brightness(0)_saturate(100%)_invert(0.45)_sepia(1)_saturate(4)_hue-rotate(95deg)]" />
              <span className="hidden sm:inline">Edit Profile</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleUpdate}
              className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-md shadow-primary-500/25 font-medium text-sm"
            >
              Update
            </button>
          )}
        </div>
      </div>

      {/* Personal Info Card */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden hover:shadow-lg transition-all duration-300">
        <div className="p-6 border-b border-gray-100/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Personal Information</h3>
              <p className="text-sm text-gray-500 mt-0.5">Your name, email, and contact details</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TextBox2 label="First Name" name="firstName" type="text" credentials={credentials} value={credentials.firstName} setValue={setCredentials} readOnly={isReadOnly} />
            <TextBox2 label="Last Name" name="lastName" type="text" credentials={credentials} value={credentials.lastName} setValue={setCredentials} readOnly={isReadOnly} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <TextBox2 label="Email" name="email" type="email" credentials={credentials} value={credentials.email} setValue={setCredentials} readOnly disabled={editMode} />
              <Link to={`/settings/${hid}/change-email`} className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                Change Email
              </Link>
            </div>
            <TextBox2 label="Phone" name="phoneNumber" type="tel" credentials={credentials} value={credentials.phoneNumber} setValue={setCredentials} readOnly={isReadOnly} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TextBox2 label="Company Name" name="companyName" type="text" credentials={credentials} readOnly={isReadOnly} />
            <div className="space-y-1.5">
              <TextBox2 label="Password" name="password" type="text" credentials={credentials} value="*************" setValue={setCredentials} readOnly />
              <Link to={`/settings/${hid}/reset-password`} className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                Change Password
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Address Card */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden hover:shadow-lg transition-all duration-300">
        <div className="p-6 border-b border-gray-100/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Address</h3>
              <p className="text-sm text-gray-500 mt-0.5">Your billing or mailing address</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TextBox2 label="Address Line 1" name="line1" type="text" credentials={address} value={address.line1} setValue={setAddress} readOnly={isReadOnly} />
            <TextBox2 label="Address Line 2" name="line2" type="text" credentials={address} value={address.line2} setValue={setAddress} readOnly={isReadOnly} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <TextBox2 label="City" name="city" type="text" credentials={address} value={address.city} setValue={setAddress} readOnly={isReadOnly} />
            <TextBox2 label="State" name="state" type="text" credentials={address} value={address.state} setValue={setAddress} readOnly={isReadOnly} />
            <TextBox2 label="Zip Code" name="zipCode" type="text" credentials={address} value={address.zipCode} setValue={setAddress} readOnly={isReadOnly} />
          </div>
        </div>
      </div>

      {/* Entry Instruction Emails Card (Integrations-style) */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden hover:shadow-lg transition-all duration-300">
        <div className="p-6 border-b border-gray-100/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Entry Instruction Emails</h3>
              <p className="text-sm text-gray-500 mt-0.5">Get Notified When Orders Need Entry Instructions and Delivery Time</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <label className="text-sm font-medium text-gray-700">Receive Entry Instruction Emails</label>
            <button
              type="button"
              role="switch"
              aria-checked={receiveEntryInstructionsEmails}
              onClick={handleToggleEntryInstructions}
              disabled={loadingFlags}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                receiveEntryInstructionsEmails ? "bg-primary-500" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  receiveEntryInstructionsEmails ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {receiveEntryInstructionsEmails && (
            <div className="rounded-xl border border-gray-200/60 bg-gray-50/50 p-4 space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Additional Notification Emails</p>
              <ul className="space-y-2">
                {entryInstructionsAdditionalEmails.map((email) => (
                  <li key={email} className="flex items-center justify-between gap-2 py-2 px-3 bg-white rounded-lg border border-gray-200/60">
                    <span className="text-sm text-gray-900 truncate">{email}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveNotificationEmail(email)}
                      disabled={loadingFlags}
                      className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                      aria-label={`Remove ${email}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="email"
                  value={newNotificationEmail}
                  onChange={(e) => setNewNotificationEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddNotificationEmail())}
                  placeholder="Add Another Email Address"
                  disabled={loadingFlags}
                  className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-gray-200/60 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={handleAddNotificationEmail}
                  disabled={loadingFlags}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  {loadingFlags ? "Saving…" : "Add Email"}
                </button>
              </div>
            </div>
          )}

          {entryEmailError && <p className="text-sm text-red-600">{entryEmailError}</p>}
          {entryEmailSuccess && <p className="text-sm text-green-600">{entryEmailSuccess}</p>}

          <p className="text-xs text-gray-500">
            You Can Also Manage This in <Link to="/integrations" className="font-medium text-primary-600 hover:text-primary-700">Integrations</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProfileComponent;
